"use client";

import { useTheme } from "next-themes";
import {
	type CSSProperties,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib";
import {
	cellsForSize,
	FADED_SQUARES_TONE_RGB,
	type FadedSquaresDropoffMode,
	type FadedSquaresSides,
	paintFadedSquares,
	resolveFadedSquaresTone,
} from "./faded-squares-math";

export type FadedSquaresVariant = "light" | "dark" | "system" | "contrast";

export interface FadedSquaresProps {
	className?: string;
	style?: CSSProperties;
	/**
	 * Square tone. `system` follows the theme (white in light / black in dark).
	 * `contrast` is the inverse — use on blank page chrome so squares stay visible.
	 */
	variant?: FadedSquaresVariant;
	/**
	 * `opacity` — every cell in the fade draws; alpha falls off from the edge.
	 * `density` — cells are randomly gated by the edge weight (blinking-squares style).
	 */
	dropoffMode?: FadedSquaresDropoffMode;
	/** Which edges contribute (max-composited). */
	sides?: FadedSquaresSides;
	/** Cells along the short axis (4–200). Default 52. */
	gridSize?: number;
	/** Square fill within each cell (0–1). Default 0.57. */
	squareSize?: number;
	/**
	 * Curve sharpness for the edge falloff. 1 = linear; higher stays strong
	 * near the edge then drops faster. Default 1.25.
	 */
	falloff?: number;
	/**
	 * Distance from an enabled edge (0–1 of the axis) where opacity/density
	 * reaches zero. `0.35` ≈ clear by the center with top+bottom. Default 0.35.
	 */
	fadeExtent?: number;
	/** Overall CSS layer opacity (0–1). Default 1. Applied on the canvas element. */
	opacity?: number;
	/** Min per-cell brightness (0–1). Default 0.55. */
	minBrightness?: number;
	/** Master brightness multiplier. Default 1. */
	intensity?: number;
	/** Cap device pixel ratio (1–2). Default 1.5. */
	dpr?: number;
}

const DEFAULT_SIDES: FadedSquaresSides = {
	top: true,
	bottom: true,
	left: false,
	right: false,
};

/**
 * Static grid of translucent squares that fade from enabled edges toward the
 * center. Paint-once canvas (no animation loop) for a light footprint.
 */
export function FadedSquares({
	className,
	style,
	variant = "system",
	dropoffMode = "opacity",
	sides = DEFAULT_SIDES,
	gridSize = 52,
	squareSize = 0.57,
	falloff = 1.25,
	fadeExtent = 0.35,
	opacity = 1,
	minBrightness = 0.55,
	intensity = 1,
	dpr = 1.5,
}: FadedSquaresProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [canvasReady, setCanvasReady] = useState(false);
	const [mounted, setMounted] = useState(false);
	const { resolvedTheme } = useTheme();

	useEffect(() => {
		setMounted(true);
	}, []);

	// Until mounted, ignore client theme so SSR HTML matches the first paint
	// (`resolveFadedSquaresTone` defaults system+undefined → light).
	const tone = resolveFadedSquaresTone(
		variant,
		mounted ? resolvedTheme : undefined,
	);

	const sideTop = sides.top === true;
	const sideRight = sides.right === true;
	const sideBottom = sides.bottom === true;
	const sideLeft = sides.left === true;

	useLayoutEffect(() => {
		const container = containerRef.current;
		const canvas = canvasRef.current;
		if (!container || !canvas) {
			return;
		}

		const ctx = canvas.getContext("2d", { alpha: true });
		if (!ctx) {
			return;
		}

		const resolvedSides: FadedSquaresSides = {
			top: sideTop,
			right: sideRight,
			bottom: sideBottom,
			left: sideLeft,
		};

		let fadeInCancelled = false;
		let resizeRaf: number | null = null;

		const paint = () => {
			const width = container.clientWidth;
			const height = container.clientHeight;
			if (width <= 0 || height <= 0) {
				return;
			}

			const pixelRatio = Math.min(
				Math.max(window.devicePixelRatio || 1, 1),
				dpr,
			);
			const bitmapW = Math.max(1, Math.round(width * pixelRatio));
			const bitmapH = Math.max(1, Math.round(height * pixelRatio));

			if (canvas.width !== bitmapW || canvas.height !== bitmapH) {
				canvas.width = bitmapW;
				canvas.height = bitmapH;
			}
			canvas.style.width = `${width}px`;
			canvas.style.height = `${height}px`;

			const { cols, rows } = cellsForSize(width, height, gridSize);
			paintFadedSquares(ctx, bitmapW, bitmapH, {
				cols,
				rows,
				squareSize,
				fadeExtent,
				falloff,
				sides: resolvedSides,
				dropoffMode,
				minBrightness,
				intensity,
				color: FADED_SQUARES_TONE_RGB[tone],
			});
		};

		setCanvasReady(false);
		paint();
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (!fadeInCancelled) {
					setCanvasReady(true);
				}
			});
		});

		const observer = new ResizeObserver(() => {
			if (resizeRaf != null) {
				cancelAnimationFrame(resizeRaf);
			}
			resizeRaf = requestAnimationFrame(() => {
				paint();
			});
		});
		observer.observe(container);

		return () => {
			fadeInCancelled = true;
			observer.disconnect();
			if (resizeRaf != null) {
				cancelAnimationFrame(resizeRaf);
			}
		};
	}, [
		tone,
		dropoffMode,
		sideTop,
		sideRight,
		sideBottom,
		sideLeft,
		gridSize,
		squareSize,
		falloff,
		fadeExtent,
		minBrightness,
		intensity,
		dpr,
	]);

	return (
		<div
			ref={containerRef}
			className={cn(
				"pointer-events-none absolute inset-0 overflow-hidden",
				className,
			)}
			style={style}
			aria-hidden
			data-testid="faded-squares"
			data-variant={variant}
			{...(mounted ? { "data-tone": tone } : {})}
			data-dropoff-mode={dropoffMode}
			data-opacity={opacity}
			suppressHydrationWarning
		>
			<canvas
				ref={canvasRef}
				className="absolute inset-0 h-full w-full transition-opacity duration-2000 ease-out motion-reduce:transition-none"
				style={{ opacity: canvasReady ? opacity : 0 }}
			/>
		</div>
	);
}
